import React from 'react';
import pick from 'lodash/pick';
import idx from 'idx';
import PageTransition from 'react-router-page-transition';
import { Form, submit } from 'redux-form';
import { LoadingIndicator } from 'common/components/preloader';
import PAPBase from 'pap/containers/common/pap_base';
import PAPQuestionBase from 'pap/components/common/pap_question_base';
import { getFieldPropsFromDefinition, getCurrentStepIndex, getVisibleBriefQuestions,
  getFeatureProjectFields, getDetailsStepLink } from 'pap/utils';
import { getSubCategory } from 'common/ducks/categories';
import { getBannerContents } from 'common/components/discount_code';
import { getStoredDiscountCode, getPendingDiscountCode } from 'pap/ducks/pricing';
import {
  trackGoogle, trackPerfectAudience, trackFacebookCustomEvent, trackMixpanel
} from 'common/tracking';
import { showAllQuestionsAtOnce } from 'pap_v2/utils';

/**
 * A component that is responsible for handling the "details" (first) phase of the PAP workflow.
 * During this phase the project does not yet exist on the server and is created when the user goes
 * through all of the steps.
 *
 * This component just like the brief component renders fields defined in a list, one at a time.
 * The list is passed as a props in the `detailsSteps` attribute.
 *
 * It is also during this phase that lazy registration takes place.
 *
 * NOTE: the data is currently not persisted until the very end (once we got a chance to create a
 * new account if the user is not logged-in).
 *
 * @author Kamil Grymuza <kamil@startupfoundry.com>
 */
class PAPDetails extends React.Component {
  constructor(props) {
    super(props);

    this.onNextStep = this.onNextStep.bind(this);
    this.onPrevStep = this.onPrevStep.bind(this);
    this.saveContractPreferences = this.saveContractPreferences.bind(this);
  }

  redirectIfNeeded(category, origin) {
    /* redirect to appropriate view in case there's missing or incomplete data */

    const {
      categorySlug, subCategorySlug, creative1to1, router, selectedPackage,
      stepName, detailsSteps, location, toggleSelectPackageSubcat
    } = this.props;

    /* 1-1 project */
    if (category.is_one_to_one) {

      /* a creative is required - redirect to PAP0 */
      if (!creative1to1) {
        router.push("/start-a-project/start-1-1/");
        return true;
      }

      /* user should always start at first step */
      if (this.currentStepIndex() > 0) {
        const firstStepLink = getDetailsStepLink(categorySlug, subCategorySlug, detailsSteps, 0);
        router.push(firstStepLink);
        return true;
      }

    /* standard project */
    } else {
      /* FIXME: all standard projects (i.e. non-1-1) are now being created through PAP v2.
         Once we have support for 1-1 projects in PAP v2:
         - remove PAP v1 and make PAP v2 the only one.
         - fix links throughout the app to use PAP v2 URLs. */
      let papV2Url = `/start-a-project/${categorySlug}/${subCategorySlug}/brief/`;
      /* V2 variation with all questions at once (for desktop only) */
      if (showAllQuestionsAtOnce()) {
        papV2Url += `all`;
      }
      if (origin !== 'direct') {
        papV2Url += `?origin=${origin}`;
      }
      router.replace(papV2Url);

      if (
        !idx(location, _ => _.state.packageSubcatSelected) &&
        ["packaging-design", "package-graphics"].includes(subCategorySlug)
      ) {
        toggleSelectPackageSubcat();
      }

      return true;
    }
  }

  componentDidMount() {
    const { fetchCategoriesIfNeeded, subCategorySlug, fetchPricing, fetchCountriesIfNeeded,
      setLazyRegistering, user, showBanner, router, stepName, reset, categorySlug,
      select1to1Creative, creative1to1 } = this.props;

    const origin = (
      idx(router.location, _ => _.state.origin) || router.location.query.origin || 'direct'
    );

    /* make sure form is clean before starting PAP */
    reset();

    fetchCategoriesIfNeeded().then(({ data }) => {
      const category = data.find((c) => c.slug === categorySlug);
      const subCategory = getSubCategory({ data }, subCategorySlug);
      const subCategoryId = subCategory.id;

      /* clear selected 1-1 creative if current category is not 1-1 */
      if (!category.is_one_to_one && creative1to1) {
        select1to1Creative(null);
      }

      /* only perform further actions of no redirect was performed */
      if (this.redirectIfNeeded(category, origin)) {
        return;
      }

      fetchCountriesIfNeeded();
      fetchPricing(subCategoryId);

      setLazyRegistering(!user.logged_in);

      /* show discount code banner if any */
      const code = getStoredDiscountCode(user.profile_data);
      const bannerContents = getBannerContents(code, 'apply');
      if (bannerContents) {
        showBanner(bannerContents.title, bannerContents.content, bannerContents.icon);
      }

      /* event tracking */
      trackFacebookCustomEvent("1726897664301628", "started_details");
      trackGoogle("pap", "started pap", origin, null);
      trackGoogle("pap", "started details", subCategory.slug, null);
    });
  }

  componentDidUpdate(prevProps) {
    const { lazyRegistering, fetchDiscountCode, showBanner } = this.props;

    /* if user just registered, retrieve pending discount code if any so that it's stored
       in the user object */
    if (prevProps.lazyRegistering && !lazyRegistering) {
      const pendingCodeSlug = getPendingDiscountCode(true);
      if (pendingCodeSlug) {
        fetchDiscountCode(pendingCodeSlug).then(({ data }) => {
          /* show banner informing user of discount code */
          const bannerContents = getBannerContents(data, 'apply');
          showBanner(bannerContents.title, bannerContents.content, bannerContents.icon);
        });
      }
    }
  }

  currentStepIndex() {
    return getCurrentStepIndex(this.props.detailsSteps, this.props.stepName);
  }

  getNextStepLink() {
    const { categorySlug, subCategorySlug, detailsSteps } = this.props;
    return getDetailsStepLink(
      categorySlug, subCategorySlug, detailsSteps, this.currentStepIndex() + 1
    );
  }

  getPrevStepLink() {
    const prevStep = this.props.detailsSteps[this.currentStepIndex() - 1];
    const stem = prevStep? prevStep.name : '';
    return `/start-a-project/${this.props.categorySlug}/${this.props.subCategorySlug}/details/${stem}`;
  }

  onNextStep() {
    if (this.props.stepName === 'package' && this.props.selectedPackage) {
      trackGoogle("pap", "selected package", this.props.selectedPackage.slug, null);
    }

    /* if lazy registering, just call `asyncValidate()` to handle account creation */
    if (this.props.stepName === "create-account") {
      this.props.asyncValidate();
      return;
    }

    if (this.currentStepIndex() === this.props.detailsSteps.length - 1) {
      this.props.dispatch(submit('pap_details'));
    } else {
      const stem = this.props.detailsSteps[this.currentStepIndex() + 1].name
      if (stem === "create-account") {
        trackMixpanel("Started registration", {"variant": "lazy"});
        trackGoogle("registration", "started registration", "lazy");
      }
      this.props.router.push(this.getNextStepLink());
    }
  }

  onPrevStep() {
    this.props.router.push({pathname: this.getPrevStepLink()});
  }

  renderStep() {
    const {
      detailsSteps, stepName, displayFullScreen, hideFullScreen, fullScreen, packages,
      countries, selectedPackage, subCategory, setLazyRegistering, user
    } = this.props;

    const fieldDefinition = detailsSteps.find(s => s.name === stepName);

    if (!fieldDefinition) {
      return null;
    }

    const fieldProps = getFieldPropsFromDefinition(fieldDefinition, {
      user: user.profile_data,
      currentFeatures: selectedPackage? selectedPackage.features : [],
      currentAwards: selectedPackage? selectedPackage.awards : [],
      currentTips: selectedPackage? selectedPackage.package_tips : [],
      stepNumber: this.currentStepIndex() + 1,
      stepCount: detailsSteps.length,
      displayFullScreen,
      hideFullScreen,
      fullScreen,
      packages,
      countries,
      subCategory,
      setLazyRegistering,
      nextStepLink: this.getNextStepLink(),
      onNextStep: this.onNextStep
    });

    return <PAPQuestionBase {...fieldProps} data-transition-id={`brief-${fieldDefinition.name}`}/>
  }

  saveContractPreferences(projectId, data) {
    /* Save ICA (Independent Contractor Agreement) contract preferences. */
    const { createCustomContractRequest, updateProjectRequest } = this.props
    const contractData = data['contract']

    /* upload custom contract if provided ... */
    if (contractData.isCustom) {
      return createCustomContractRequest(projectId, contractData['custom_contract'])
    }

    /* or update project with information that will be used to create a contract
       out of CS templates */
    return updateProjectRequest(
      projectId, pick(contractData, ['client_legal_name', 'client_legal_country'])
    )
  }

  handleSubmit(data) {
    const {
      dispatch, router, resetPAP, setUnhandledError, selectedPackage, categorySlug,
      updateFeaturesRequest, subCategory, categories, createProjectRequest,
      creative1to1, awardValue1to1, updateAwardsRequest, user, logout, detailsSteps
    } = this.props;

    /* prevent submission if hitting Enter key during account creation */
    if (this.currentStepIndex() < detailsSteps.length - 1) {
      return;
    }

    const projectData = {
      sub_category: subCategory.id,
      title: data.title,
      original_package: selectedPackage.id,
      one_to_one_creative: creative1to1? creative1to1.id : undefined,
      one_to_one_negotiation_value: awardValue1to1 || undefined,
      /* project fields that can be changed once features are activated */
      ...getFeatureProjectFields(data)
    }

    return createProjectRequest(projectData).then((response) => {
      /* update project package awards */
      return updateAwardsRequest(response.data.id, response.data['award_rules'], data).then(() => {
        /* update project package features */
        return updateFeaturesRequest(
          response.data.id, selectedPackage.features, data, null, user.profile_data
        ).then(() => {
          /* save contract preferences */
          return this.saveContractPreferences(response.data.id, data).then(() => {
            /* clear PAP state and move on to first brief question */
            dispatch(resetPAP());
            const firstQuestion = getVisibleBriefQuestions(response.data, true)[0].name;
            router.push({pathname: `/start-a-project/${response.data.id}/brief/${firstQuestion}`});

            trackGoogle("pap", "created draft", subCategory.slug);
            trackGoogle("pap", "started brief", subCategory.slug);
            trackPerfectAudience("posted_draft");
            trackPerfectAudience(`posted_${categorySlug.replace('1to1-', '').replace(/-/g, '_')}_draft`);
            trackFacebookCustomEvent("1726897664301628", "posted_draft");
          })
        })
      })
    }).catch((response) => {
      if (response.status === 403) {
        logout(undefined, true);
        return;
      }

      dispatch(setUnhandledError("Error occured while saving the project", response));
    })
  }

  render () {
    const { handleSubmit, packages, categories, submitting, detailsSteps,
            valid, selectedPackage } = this.props

    if (!packages || categories.length == 0 || submitting) {
      return <LoadingIndicator />
    }

    return <PAPBase
      sectionName="details"
      extraClasses={["details"]}
      currentStepIndex={this.currentStepIndex()}
      currentSectionStepCount={detailsSteps.length}
      onPrevStep={this.onPrevStep}
      onNextStep={this.onNextStep}
      nextEnabled={valid}
      prevEnabled={this.currentStepIndex() > 0}
      currentFeatures={selectedPackage? selectedPackage.features : []}
      currentAwards={selectedPackage? selectedPackage.awards : []}
      currentTips={selectedPackage? selectedPackage.package_tips : []}
      {...this.props}>
        <Form onSubmit={handleSubmit(this.handleSubmit.bind(this))}>
          <PageTransition timeout={500}>
            { this.renderStep() }
          </PageTransition>
        </Form>
      </PAPBase>
  }
}

export default PAPDetails



// WEBPACK FOOTER //
// ./pap/components/details.jsx